/**
 * Personal records, as the gym counts them: heavier than ever, or more reps
 * than ever at that load. One rule covers both — no earlier set was at least
 * as heavy with at least as many reps.
 *
 * Only sets logged with their reps count: the sheet's history tab has loads
 * alone, which cannot say whether 8 × 13 was ever done.
 */

import type { RecordMargin } from "./schema.ts";
import { round } from "./units.ts";

/** A set as far as records go. */
export interface Lift {
  weightKg: number;
  reps: number;
}

/** False the very first time: a record has to beat something. */
export function isRecord(earlier: readonly Lift[], lift: Lift): boolean {
  if (earlier.length === 0) return false;
  return !earlier.some((other) => other.weightKg >= lift.weightKg && other.reps >= lift.reps);
}

/**
 * The few earlier lifts that `isRecord` needs: those no other one beats on
 * both counts, heaviest first. A year of sets fits in a handful.
 */
export function bestLifts(lifts: readonly Lift[]): Lift[] {
  const sorted = [...lifts].sort((a, b) => b.weightKg - a.weightKg || b.reps - a.reps);
  const front: Lift[] = [];
  for (const lift of sorted) {
    if (lift.reps > (front.at(-1)?.reps ?? -1)) front.push(lift);
  }
  return front;
}

/**
 * What a record adds to what came before, the way it is said in the gym:
 * "+2,5 kg" for a load never lifted, "+3 reps" at a load already known.
 * Null when `lift` is no record.
 */
export function recordMargin(earlier: readonly Lift[], lift: Lift): RecordMargin | null {
  if (!isRecord(earlier, lift)) return null;
  const asHeavy = earlier.filter((other) => other.weightKg >= lift.weightKg);
  if (asHeavy.length === 0) {
    return { kind: "load", kg: round(lift.weightKg - Math.max(...earlier.map((other) => other.weightKg)), 1) };
  }
  return { kind: "reps", reps: lift.reps - Math.max(...asHeavy.map((other) => other.reps)) };
}
